// app/javascript/controllers/dae20/live_table_controller.js
import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

/**
 * Stimulus Controller для live-reload таблиц.
 *
 * Подписывается на ActionCable канал Dae20::LiveTableChannel.
 * При получении сигнала rows_changed — запрашивает у сервера
 * обновлённые строки как TurboStream и применяет к DOM.
 * Затем добавляет CSS-анимацию подсветки на 60 секунд.
 *
 * Использование:
 * <div data-controller="dae20--live-table"
 *      data-dae20--live-table-source-code-value="products"
 *      data-dae20--live-table-component-id-value="123"
 *      data-dae20--live-table-highlight-duration-value="60000">
 *   <div data-dae20--live-table-target="tableBody">
 *     <!-- Строки таблицы -->
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ["tableBody"]

  static values = {
    sourceCode: String,
    componentId: String,
    highlightDuration: { type: Number, default: 60000 }
  }

  subscription = null
  consumer = null
  highlightTimers = new Map()

  connect() {
    if (!this.sourceCodeValue) {
      console.warn("[LiveTable] No sourceCode — skipping subscription")
      return
    }

    console.log("[LiveTable] Connecting", {
      sourceCode: this.sourceCodeValue,
      componentId: this.componentIdValue
    })

    this.subscribe()
  }

  disconnect() {
    this.unsubscribe()
    this.clearAllHighlightTimers()
  }

  // === Subscription ===

  subscribe() {
    if (this.subscription) return

    try {
      this.consumer = this.consumer || createConsumer()

      this.subscription = this.consumer.subscriptions.create(
        {
          channel: "Dae20::LiveTableChannel",
          source_code: this.sourceCodeValue
        },
        {
          connected: () => {
            console.log("[LiveTable] Subscribed to", this.sourceCodeValue)
          },
          disconnected: () => {
            console.log("[LiveTable] Disconnected from", this.sourceCodeValue)
          },
          received: (data) => this.handleReceived(data),
          rejected: () => {
            console.warn("[LiveTable] Subscription rejected for", this.sourceCodeValue)
          }
        }
      )
    } catch (error) {
      console.error("[LiveTable] Subscribe error:", error)
    }
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  // === Message Handling ===

  handleReceived(data) {
    console.log("[LiveTable] Received:", data)

    if (data.type === "rows_changed") {
      if (data.action === "delete") {
        this.removeRowsFromDom(data.pks)
        return
      }
      this.fetchUpdatedRows(data.pks, data.action)
    }
  }

  removeRowsFromDom(pks) {
    if (!pks || pks.length === 0) return

    pks.forEach(pk => {
      const rowId = `live_row_${this.sourceCodeValue}_${pk}`
      const rowEl = document.getElementById(rowId)
      if (rowEl) rowEl.remove()
    })
  }

  async fetchUpdatedRows(pks, action) {
    if (!pks || pks.length === 0) return

    // Для create и pks=["all"] — загружаем все строки и заменяем tableBody целиком.
    // Для create: новой строки ещё нет в DOM, PK может не совпасть с _identity
    //   (enhanced format оборачивает PK в Hash), поэтому проще обновить всё.
    // Для pks=["all"]: массовое обновление, тоже заменяем всё.
    const refreshAll = action === "create" ||
                       (pks.length === 1 && String(pks[0]) === "all")

    try {
      const params = new URLSearchParams()
      params.append("component_id", this.componentIdValue)
      if (refreshAll) {
        params.append("all", "1")
      } else {
        pks.forEach(pk => params.append("pks[]", pk))
      }

      const url = `/dae20/live_table_rows?${params.toString()}`

      const response = await fetch(url, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        console.warn("[LiveTable] Fetch failed:", response.status)
        return
      }

      const html = await response.text()

      if (html.trim().length === 0) {
        console.log("[LiveTable] Empty response — no matching rows")
        return
      }

      if (refreshAll) {
        // Заменяем всё содержимое tableBody
        this.replaceAllRows(html)
      } else {
        // Применяем TurboStream replace (Turbo обрабатывает <turbo-stream> теги)
        Turbo.renderStreamMessage(html)
      }

      // Подсветка конкретных строк (не при полном обновлении)
      if (!refreshAll && pks.length > 0) {
        requestAnimationFrame(() => {
          pks.forEach(pk => this.applyHighlight(pk))
        })
      }
    } catch (error) {
      console.error("[LiveTable] fetchUpdatedRows error:", error)
    }
  }

  replaceAllRows(html) {
    // Парсим TurboStream, извлекаем все строки и заменяем содержимое tableBody
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")
    const streamElements = doc.querySelectorAll("turbo-stream")

    if (this.hasTableBodyTarget) {
      // Собираем все строки из TurboStream ответа
      const fragment = document.createDocumentFragment()
      streamElements.forEach(stream => {
        const template = stream.querySelector("template")
        if (template) {
          fragment.appendChild(template.content.cloneNode(true))
        }
      })

      if (fragment.childNodes.length > 0) {
        // Заменяем содержимое tableBody полностью
        this.tableBodyTarget.innerHTML = ""
        this.tableBodyTarget.appendChild(fragment)
        console.log("[LiveTable] Replaced all rows:", fragment.childNodes.length, "elements")
      }
    } else {
      // Fallback: пусть Turbo сам разберётся
      Turbo.renderStreamMessage(html)
    }
  }

  // === Highlight ===

  applyHighlight(pk) {
    const sourceCode = this.sourceCodeValue
    const rowId = `live_row_${sourceCode}_${pk}`
    const rowEl = document.getElementById(rowId)

    if (!rowEl) {
      console.log("[LiveTable] Row not found for highlight:", rowId)
      return
    }

    // Убираем старую подсветку если есть
    rowEl.classList.remove("live-row-highlight")
    // Force reflow для рестарта анимации
    void rowEl.offsetWidth
    rowEl.classList.add("live-row-highlight")

    // Очищаем старый таймер
    const existingTimer = this.highlightTimers.get(pk)
    if (existingTimer) clearTimeout(existingTimer)

    // Ставим таймер на удаление класса
    const timer = setTimeout(() => {
      rowEl.classList.remove("live-row-highlight")
      this.highlightTimers.delete(pk)
    }, this.highlightDurationValue)

    this.highlightTimers.set(pk, timer)
  }

  clearAllHighlightTimers() {
    this.highlightTimers.forEach(timer => clearTimeout(timer))
    this.highlightTimers.clear()
  }

  // === Helpers ===

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
